import { Button, Icon, IconButton, useThemeColor, useToast, Tooltip } from '@wistia/vhs';
import * as Popover from '@radix-ui/react-popover';
import styled from 'styled-components';
import type { EmojiReactions } from '~/graphql';
import type { ApolloCache } from '@apollo/client';
import type { JSX } from 'react';
import { useAddCommentReactionMutation } from './__gql/CommentReactions.gql.gen';
import { REACTIONS, generateEmojiString } from './helpers';

const SelectionContainer = styled.div`
  align-items: center;
  background: white;
  border-radius: ${({ theme }) => theme.spacing.space01};
  box-shadow: ${({ theme }) => theme.shadow.elevation2};
  display: flex;
  gap: ${({ theme }) => theme.spacing.space01};
  padding: ${({ theme }) => theme.spacing.space02};
`;

const ReactionButton = styled(Button)`
  border-radius: ${({ theme }) => theme.spacing.space01};
  height: ${({ theme }) => theme.spacing.space06};
  padding: ${({ theme }) => theme.spacing.space01};
  width: ${({ theme }) => theme.spacing.space06};
`;

/* eslint-disable camelcase */
const tooltipLabels: Record<EmojiReactions, string> = {
  thumbs_up: ':thumbs-up:',
  thumbs_down: ':thumbs-down:',
  plus: ':plus-sign:',
  laugh: ':joy:',
  party_popper: ':party-popper:',
};
/* eslint-enable camelcase */

export const CommentReactionSelect = ({ commentId }: { commentId: string }): JSX.Element => {
  const grey500 = useThemeColor('grey500');
  const showToast = useToast();

  const [addCommentReaction] = useAddCommentReactionMutation({
    update: (cache: ApolloCache<unknown>) => {
      cache.evict({ id: commentId, fieldName: 'commentReactions' });
    },
    onError: () => {
      showToast({
        message: 'There was an error reacting to the comment.',
        variant: 'error',
      });
    },
  });

  const reactToComment = (emoji: EmojiReactions) => {
    void addCommentReaction({
      variables: {
        input: {
          teamCommentId: commentId,
          emoji,
        },
      },
    });
  };

  return (
    <Popover.Root>
      <Popover.Trigger asChild={true}>
        <IconButton
          colorOverride={grey500}
          icon={<Icon type="react" />}
          label="Add reaction..."
          tooltipDirection="right"
          size="md"
        />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          sideOffset={16}
          align="start"
        >
          <SelectionContainer>
            {Object.entries(REACTIONS).map(([emoji]) => (
              <Tooltip
                key={emoji}
                label={tooltipLabels[emoji as EmojiReactions]}
              >
                <ReactionButton
                  variant="tertiary"
                  label={generateEmojiString(emoji as EmojiReactions)}
                  onClick={() => reactToComment(emoji as EmojiReactions)}
                />
              </Tooltip>
            ))}
          </SelectionContainer>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
